import { memo, useCallback, useState } from "react";
import type { FormEvent } from "react";

import { useLocalization } from "@fluent/react";
import { Button, Chip, DateInput, Popover, Row } from "@narmi/design_system";

import {
  DATE_PRESETS,
  FORMAT_DATE_DISPLAY,
  isAfterToday,
  isRangeTooLarge,
  MAX_DATE_RANGE_MONTHS,
} from "../../constants";

import styles from "./DateRangeFilterPopover.module.scss";

import type { DatePreset } from "../../constants";

export interface DateRangeSelection {
  startDate: string;
  endDate: string;
  preset: DatePreset | null;
}

interface DateRangeFilterPopoverProps {
  startDate: string;
  endDate: string;
  selectedPreset: DatePreset | null;
  onChange: (selection: DateRangeSelection | null) => void;
}

// re-rendering DateInput re-inits flatpickr, which drops focus in static mode
const MemoizedDateInput = memo(DateInput) as typeof DateInput;

const disableFutureDates = [isAfterToday];

function isCompleteDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Find which preset (if any) exactly matches the given start/end dates.
 */
function findMatchingPreset(
  startDate: string,
  endDate: string
): DatePreset | null {
  return (
    DATE_PRESETS.find(
      (p) => p.minDate() === startDate && p.maxDate() === endDate
    ) ?? null
  );
}

const DateRangeFilterPopover = ({
  startDate,
  endDate,
  selectedPreset,
  onChange,
}: DateRangeFilterPopoverProps) => {
  const { l10n } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);

  const [draft, setDraft] = useState<DateRangeSelection>({
    startDate,
    endDate,
    preset: selectedPreset,
  });

  // kept separate from draft: re-seeding the inputs per keystroke re-inits flatpickr and drops focus
  const [inputDefaults, setInputDefaults] = useState({
    startDate,
    endDate,
    version: 0,
  });

  const hasCompleteDates =
    isCompleteDate(draft.startDate) && isCompleteDate(draft.endDate);
  const hasDateRangeError = hasCompleteDates && draft.startDate > draft.endDate;
  const rangeTooLarge =
    hasCompleteDates && isRangeTooLarge(draft.startDate, draft.endDate);

  let dateRangeErrorMessage: string | undefined;
  if (hasDateRangeError) {
    dateRangeErrorMessage = l10n.getString(
      "transaction-export-date-filter-date-range-error"
    );
  } else if (rangeTooLarge) {
    dateRangeErrorMessage = l10n.getString(
      "transaction-export-error-date-range-too-large",
      { months: MAX_DATE_RANGE_MONTHS }
    );
  }

  const isApplyDisabled =
    !hasCompleteDates || hasDateRangeError || rangeTooLarge;

  const handleOpen = () => {
    setDraft({ startDate, endDate, preset: selectedPreset });
    setInputDefaults((prev) => ({
      startDate,
      endDate,
      version: prev.version + 1,
    }));
    setIsOpen(true);
  };

  const handleDismiss = () => {
    setIsOpen(false);
  };

  const handlePresetClick = (preset: DatePreset) => {
    const newStart = preset.minDate();
    const newEnd = preset.maxDate();
    setDraft({ startDate: newStart, endDate: newEnd, preset });
    setInputDefaults((prev) => ({
      startDate: newStart,
      endDate: newEnd,
      version: prev.version + 1,
    }));
  };

  const handleStartDateChange = useCallback((value: string) => {
    setDraft((prev) => ({
      startDate: value,
      endDate: prev.endDate,
      preset: findMatchingPreset(value, prev.endDate),
    }));
  }, []);

  const handleEndDateChange = useCallback((value: string) => {
    setDraft((prev) => ({
      startDate: prev.startDate,
      endDate: value,
      preset: findMatchingPreset(prev.startDate, value),
    }));
  }, []);

  const handleStartDateInput = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      handleStartDateChange(event.currentTarget.value);
    },
    [handleStartDateChange]
  );

  const handleEndDateInput = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      handleEndDateChange(event.currentTarget.value);
    },
    [handleEndDateChange]
  );

  const handleSelectCustomRange = () => {
    setDraft((prev) => ({ ...prev, preset: null }));
  };

  const handleApply = () => {
    onChange(draft);
    setIsOpen(false);
  };

  const handleRemoveFilter = () => {
    onChange(null);
    setIsOpen(false);
  };

  let chipLabel: string;
  if (selectedPreset) {
    chipLabel = l10n.getString(selectedPreset.ftlId);
  } else if (startDate && endDate) {
    chipLabel = `${FORMAT_DATE_DISPLAY(startDate)} - ${FORMAT_DATE_DISPLAY(
      endDate
    )}`;
  } else {
    chipLabel = l10n.getString("transaction-export-date-filter-default-label");
  }

  const popoverContent = (
    <div className={styles["date-filter-popover"]}>
      <span className={styles["date-filter-popover-heading"]}>
        {l10n.getString("transaction-export-date-filter-presets-heading")}
      </span>
      <div className={styles["date-filter-popover-options"]}>
        {DATE_PRESETS.map((preset) => (
          <label
            key={preset.value}
            className={styles["date-filter-popover-radio-row"]}
          >
            <input
              type="radio"
              name="date-preset"
              value={preset.value}
              checked={draft.preset?.value === preset.value}
              onChange={() => handlePresetClick(preset)}
              className={styles["date-filter-popover-radio"]}
            />
            <span>{l10n.getString(preset.ftlId)}</span>
          </label>
        ))}
      </div>

      <div className={styles["date-filter-popover-divider"]} />

      <label className={styles["date-filter-popover-radio-row"]}>
        <input
          type="radio"
          name="date-preset"
          value="custom"
          checked={draft.preset === null}
          onChange={handleSelectCustomRange}
          className={styles["date-filter-popover-radio"]}
        />
        <span>
          {l10n.getString("transaction-export-date-filter-custom-option")}
        </span>
      </label>

      {/*
        defaultDate not value (a controlled value reverts typed keystrokes);
        onInput not blur-sync (flatpickr's outside-click swallows onChange);
        error rendered below, not via the error prop (prop flips re-init
        flatpickr and drop focus); no altInput (reopens the calendar in
        static mode); type="text" (native date widget ignores dateFormat).
      */}
      <div className={styles["date-filter-popover-date-inputs"]}>
        <MemoizedDateInput
          key={`date-filter-start-${inputDefaults.version}`}
          label={l10n.getString("transaction-export-date-filter-start")}
          defaultDate={inputDefaults.startDate}
          onChange={handleStartDateChange}
          onInput={handleStartDateInput}
          dateFormat="Y-m-d"
          type="text"
          disablePortal
          testId="date-filter-start"
        />
        <MemoizedDateInput
          key={`date-filter-end-${inputDefaults.version}`}
          label={l10n.getString("transaction-export-date-filter-end")}
          defaultDate={inputDefaults.endDate}
          onChange={handleEndDateChange}
          onInput={handleEndDateInput}
          disableDates={disableFutureDates}
          dateFormat="Y-m-d"
          type="text"
          disablePortal
          testId="date-filter-end"
        />
      </div>

      {dateRangeErrorMessage && (
        <div
          className={styles["date-filter-popover-error"]}
          data-testid="date-filter-error"
        >
          {dateRangeErrorMessage}
        </div>
      )}

      <div className={styles["date-filter-popover-divider"]} />

      <div className="padding--x--s padding--y--xs">
        <Row justifyContent="space-between" alignItems="center">
          <Button
            kind="plain"
            size="xs"
            label={l10n.getString("transaction-export-date-filter-remove")}
            onClick={handleRemoveFilter}
            type="button"
            testId="date-filter-remove"
          />
          <Button
            kind="primary"
            size="xs"
            label={l10n.getString("transaction-export-date-filter-apply")}
            onClick={handleApply}
            disabled={isApplyDisabled}
            testId="date-filter-apply"
          />
        </Row>
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      alignment="start"
      isOpen={isOpen}
      onUserDismiss={handleDismiss}
      testId="date-filter-popover"
    >
      <Chip
        kind="secondary"
        label={chipLabel}
        startIcon="calendar"
        onClick={handleOpen}
        endIcon={`chevron-${isOpen ? "up" : "down"}`}
      />
    </Popover>
  );
};

export default DateRangeFilterPopover;
